const en = {
  // Navigation
  'nav.home': 'Home',
  'nav.about': 'About',
  'nav.directory': 'Directory',
  'nav.knowledge': 'Knowledge Hub',
  'nav.impact': 'Global Impact',
  'nav.contact': 'Contact',
  'nav.join': 'Join Us',

  // Hero Section
  'hero.title': 'Professional Support Network for Integration in the Netherlands',
  'hero.subtitle': 'Providing material and moral support to people of all races, languages, religions, and beliefs at every stage from refugee camp to citizenship',
  'hero.cta.primary': 'Join Our Network',
  'hero.cta.secondary': 'Explore More',

  // About Section
  'about.title': 'About Us',
  'about.subtitle': 'A Global Community Supporting Integration for Everyone Coming to the Netherlands',
  'about.mission.title': 'Our Mission',
  'about.mission.text': 'Stichting IPN aims to provide comprehensive material and moral support to people of all races, languages, religions, and beliefs coming to the Netherlands, from their arrival at refugee camps through the waiting period, finding housing, obtaining citizenship, and beyond, to ensure successful integration into Dutch society.',
  'about.vision.title': 'Our Vision',
  'about.vision.text': 'To create a society in the Netherlands where everyone has equal opportunities, cultural diversity is seen as wealth, and integration is successfully achieved.',
  'about.values.title': 'Our Values',
  'about.values.text': 'Inclusivity, solidarity, professionalism, and social responsibility are the cornerstones of our organization, reflected in everything we do.',

  // Core Values
  'values.title': 'Our Core Values',
  'values.subtitle': 'Principles That Guide Our Work',
  'values.ethics.title': 'Inclusivity',
  'values.ethics.text': 'Equal support and respect for people of all races, languages, religions, and beliefs',
  'values.wisdom.title': 'Solidarity',
  'values.wisdom.text': 'A culture of social cohesion and mutual assistance',
  'values.service.title': 'Professional Support',
  'values.service.text': 'Integration support through expert guidance and practical solutions',
  'values.impact.title': 'Social Impact',
  'values.impact.text': 'Creating lasting and meaningful change in Dutch society',

  // Professional Directory
  'directory.title': 'Professional Directory',
  'directory.subtitle': 'Connect with Experts Who Can Help You in Your Integration Process',
  'directory.search': 'Search professionals...',
  'directory.filter.all': 'All Fields',
  'directory.filter.education': 'Education',
  'directory.filter.healthcare': 'Healthcare',
  'directory.filter.technology': 'Technology',
  'directory.filter.business': 'Business',
  'directory.filter.law': 'Law',
  'directory.filter.engineering': 'Engineering',
  'directory.viewProfile': 'View Profile',

  // Knowledge Hub
  'knowledge.title': 'Knowledge and Resource Center',
  'knowledge.subtitle': 'Guides, Articles, and Resources for Your Integration Process',
  'knowledge.featured': 'Featured Publications',
  'knowledge.readMore': 'Read More',
  'knowledge.viewAll': 'View All Publications',

  // Global Impact
  'impact.title': 'Our Support Programs',
  'impact.subtitle': 'We Are With You at Every Stage of Your Integration Journey',
  'impact.education.title': 'Education and Language Support',
  'impact.education.text': 'Dutch language courses, educational counseling, and guidance in diploma equivalency processes',
  'impact.healthcare.title': 'Health and Social Support',
  'impact.healthcare.text': 'Healthcare system guidance, psychological support, and social assistance programs',
  'impact.research.title': 'Employment and Career Support',
  'impact.research.text': 'Job search, CV preparation, vocational training, and entrepreneurship consulting',
  'impact.humanitarian.title': 'Legal and Administrative Support',
  'impact.humanitarian.text': 'Professional guidance in residence permits, citizenship applications, and legal processes',

  // Testimonials
  'testimonials.title': 'From Our Members',
  'testimonials.subtitle': 'Voices from Our Community',

  // Contact
  'contact.title': 'Get in Touch',
  'contact.subtitle': 'Have questions? We\'d love to hear from you',
  'contact.name': 'Your Name',
  'contact.email': 'Your Email',
  'contact.subject': 'Subject',
  'contact.message': 'Your Message',
  'contact.send': 'Send Message',
  'contact.info.title': 'Contact Information',
  'contact.info.address': 'Address',
  'contact.info.phone': 'Phone',
  'contact.info.email': 'Email',

  // Footer
  'footer.about.title': 'About Stichting IPN',
  'footer.about.text': 'A professional network providing comprehensive support to all people coming to the Netherlands during their integration process.',
  'footer.quickLinks': 'Quick Links',
  'footer.connect': 'Stay Connected',
  'footer.newsletter': 'Subscribe to Our Newsletter',
  'footer.newsletter.placeholder': 'Enter your email',
  'footer.newsletter.button': 'Subscribe',
  'footer.copyright': '© 2024 Stichting IPN. All rights reserved.',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',

  integration: {
    title: 'Our Integration Process',
    subtitle: 'We are with you at every step from your arrival in the Netherlands to citizenship. Here is the 6-stage process we will guide you through.',
    step: 'Step',
    requiredDocuments: 'Required Documents',
    tips: 'Important Tips',
    getSupport: 'Get Support at This Stage',
    successTitle: 'Discover Success Stories',
    successDescription: 'Thousands of people have successfully completed this process. Get inspired by their experiences and succeed too.',
    readStories: 'Read Stories',
    
    steps: {
      step1: {
        shortTitle: 'Arrival',
        title: 'Arrival & Initial Registration',
        description: 'Initial registration at Ter Apel and temporary accommodation arrangements',
        duration: '1-3 days',
        documents: [
          'Passport or identity document',
          'Travel documents',
          'Education and work documents if available',
          'Documents showing family status'
        ],
        tips: [
          'Keep all your documents in a safe place',
          'Be honest and open in the first interview',
          'Use interpreter services',
          'Listen carefully to the information provided'
        ]
      },
      step2: {
        shortTitle: 'Interview',
        title: 'IND Interviews',
        description: 'Asylum application and detailed interviews, telling your story',
        duration: '6-12 months',
        documents: [
          'Identity documents and photographs',
          'Documents supporting reasons for leaving your country',
          'Evidence of threats or persecution if available',
          'Documents showing family ties'
        ],
        tips: [
          'Tell your story consistently and in detail',
          'Answer all questions carefully',
          'Get legal support',
          'Use psychological support services'
        ]
      },
      step3: {
        shortTitle: 'Settlement',
        title: 'Residence Permit & Housing',
        description: 'Obtaining residence permit and permanent housing allocation, beginning of your new life',
        duration: '2-6 months',
        documents: [
          'IND decision and residence permit',
          'BSN (Burgerservicenummer) registration',
          'Health insurance certificate',
          'Bank account information'
        ],
        tips: [
          'Register with the municipality immediately',
          'Get health insurance (mandatory)',
          'Apply for social assistance',
          'Meet your neighborhood and neighbors'
        ]
      },
      step4: {
        shortTitle: 'Education',
        title: 'Language & Integration Training',
        description: 'Dutch language course and social integration programs, cultural adaptation',
        duration: '6-36 months',
        documents: [
          'Integration exam registration document',
          'Language course attendance certificate',
          'Education progress reports',
          'Exam result documents'
        ],
        tips: [
          'Attend courses regularly',
          'Practice Dutch at home too',
          'Participate in local events',
          'Apply for volunteer work'
        ]
      },
      step5: {
        shortTitle: 'Employment',
        title: 'Job Search & Career',
        description: 'Vocational training, job search support and career planning services',
        duration: '3-12 months',
        documents: [
          'CV and motivation letter',
          'Diploma equivalency certificate',
          'Job application documents',
          'Reference letters'
        ],
        tips: [
          'Get your diplomas recognized',
          'Prepare a Dutch-style CV',
          'Expand your professional network',
          'Consider internship and volunteer opportunities'
        ]
      },
      step6: {
        shortTitle: 'Citizenship',
        title: 'Citizenship & Beyond',
        description: 'Dutch citizenship application and full integration into society',
        duration: '5+ years',
        documents: [
          '5-year residence permit document',
          'Integration exam success certificate',
          'Income and tax documents',
          'Criminal record certificate'
        ],
        tips: [
          'Fulfill all legal requirements',
          'Show active participation in society',
          'Meet your financial responsibilities',
          'Embrace Dutch values'
        ]
      }
    }
  },
  contact: {
    form: {
      name: 'Name',
      namePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email address',
      phone: 'Phone',
      phonePlaceholder: 'Enter your phone number',
      subject: 'Subject',
      subjectPlaceholder: 'Subject of your message',
      message: 'Message',
      messagePlaceholder: 'Write your message here...',
      maxChars: 'Maximum 500 characters',
      submit: 'Send Message',
      sending: 'Sending...',
      successMessage: 'Your message has been sent successfully! We will get back to you as soon as possible.',
      errorMessage: 'An error occurred. Please try again.',
    },
    workingHours: 'Working Hours',
    weekdays: 'Weekdays',
    weekend: 'Weekend',
    closed: 'Closed',
  },
  news: {
    title: 'Refugee News Tracking System',
    subtitle: 'Follow the latest news about refugees from IND, COA, municipalities, and media. Subscribe to our monthly newsletter and never miss important developments.',
    subscribe: 'Subscribe to Monthly Newsletter',
    new: 'New',
    readMore: 'Read More',
    categories: {
      all: 'All',
      municipality: 'Municipalities',
      media: 'Media',
      policy: 'Policy',
    },
    items: {
      ind1: {
        title: 'IND Announces New Application Procedures',
        summary: 'IND introduces new procedures and faster assessment processes for asylum applications. Important changes for applicants.',
      },
      coa1: {
        title: 'COA Opens New Reception Centers',
        summary: 'COA announced opening new reception centers across the Netherlands to respond to increased demand. Capacity increases by 30%.',
      },
      municipality1: {
        title: 'Amsterdam Expands Integration Programs',
        source: 'Amsterdam Municipality',
        summary: 'Amsterdam Municipality increases investments in language courses and employment programs for refugees. New budget approved.',
      },
      media1: {
        title: 'Refugee Integration in the Netherlands: Success Stories',
        summary: 'NOS released a special documentary about successful integration examples and contributions of refugees to Dutch society.',
      },
      policy1: {
        title: 'New Integration Law Approved in Parliament',
        summary: 'Dutch Parliament approved a new law that facilitates and supports the integration process for refugees. Law takes effect in 2024.',
      },
      ind2: {
        title: 'IND Launches Digital Application System',
        summary: 'IND introduced a new platform allowing asylum applications to be submitted digitally. The process is now faster and more transparent.',
      },
    },
    infoBox: {
      title: 'Automatic News Tracking',
      description: 'Our system scans IND, COA, Overheid.nl, municipalities, and leading media organizations daily and automatically compiles important news about refugees. Subscribe to our monthly newsletter to stay informed about all important developments.',
    },
    modal: {
      title: 'Subscribe to Monthly Newsletter',
      description: 'Enter your email address and receive the most important news about refugees directly in your inbox every month.',
      emailLabel: 'Email Address',
      emailPlaceholder: 'example@email.com',
      subscribeButton: 'Subscribe',
      subscribing: 'Subscribing...',
      successMessage: 'You have successfully subscribed! You will receive your first newsletter soon.',
      errorMessage: 'An error occurred. Please try again.',
    },
  }
};

export default en;